import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, SUB_STATE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Ban, Play, CalendarPlus, ExternalLink, Search, Settings2, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function AdminBusinesses() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { impersonate } = useAuth();
  const { data: businesses = [], isLoading } = useQuery({ queryKey: ["admin-businesses"], queryFn: () => api.get("/admin/businesses").then((r) => r.data) });
  const { data: adminPlans = [] } = useQuery({ queryKey: ["admin-plans"], queryFn: () => api.get("/admin/plans").then((r) => r.data) });
  const [q, setQ] = useState("");
  const [assign, setAssign] = useState(null);
  const [planId, setPlanId] = useState("");
  const [busy, setBusy] = useState(false);

  const subPlans = adminPlans.filter((p) => p.type === "subscription");

  const manage = async (id) => {
    setBusy(true);
    try {
      await impersonate(id);
      nav("/panel");
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  const act = async (id, action, extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/admin/businesses/${id}`, { action, ...extra });
      qc.invalidateQueries({ queryKey: ["admin-businesses"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("Güncellendi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); setAssign(null); }
  };

  const addCredits = async (b) => {
    const val = window.prompt(`"${b.name}" işletmesine kaç WhatsApp kontörü yüklensin?`, "50");
    if (val === null) return;
    const amount = parseInt(val, 10);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Geçerli bir sayı girin"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/businesses/${b.id}`, { action: "add_credits", credits: amount });
      qc.invalidateQueries({ queryKey: ["admin-businesses"] });
      toast.success(`${amount} kontör yüklendi`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`"${name}" işletmesi ve TÜM verileri (hizmetler, personel, rezervasyonlar, sahip hesabı) kalıcı olarak silinecek.\n\nEmin misiniz?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/businesses/${id}`);
      qc.invalidateQueries({ queryKey: ["admin-businesses"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("İşletme silindi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const filtered = businesses.filter((b) =>
    (b.name + b.owner_email + b.slug).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="admin-businesses-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">İşletmeler</h1>
          <p className="text-muted-foreground mt-1">Başvuruları ve abonelikleri yönetin</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="İşletme veya e-posta ara"
            className="h-11 w-64 max-w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm" data-testid="admin-search" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">Kayıt bulunamadı.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => {
            const st = SUB_STATE[b.subscription_state] || SUB_STATE.free;
            const fmtLastLogin = (iso) => {
              if (!iso) return "hiç giriş yok";
              const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
              if (days <= 0) return "bugün";
              if (days === 1) return "dün";
              return `${days} gün önce`;
            };
            return (
              <div key={b.id} className="rounded-xl border border-border bg-card p-4" data-testid="admin-business-row">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{b.name}</p>
                      <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full border", st.cls)}>
                        {st.label}{b.subscription_state === "trial" ? ` · ${b.trial_days_left}g` : ""}
                      </span>
                      {b.plan_name && <span className="text-xs text-muted-foreground">· {b.plan_name}</span>}
                      {b.is_suspended && b.suspended_reason === "inactivity_30d" && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600" data-testid="auto-passive-badge">Otomatik pasif</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{b.owner_email} · /r/{b.slug}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{b.services_count} hizmet · {b.reservations_count} rezervasyon · <span className="font-semibold text-foreground" data-testid="admin-link-clicks">{b.link_clicks || 0}</span> link tıklaması · <span className="font-semibold text-foreground" data-testid="admin-wa-credits">{b.wa_credits ?? 0}</span> kontör</p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid="admin-last-login">
                      Son giriş: <span className={cn("font-semibold", fmtLastLogin(b.last_login_at).includes("gün önce") && parseInt(fmtLastLogin(b.last_login_at)) >= 30 ? "text-red-600" : "text-foreground")}>{fmtLastLogin(b.last_login_at)}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`/r/${b.slug}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" data-testid="admin-view-link"><ExternalLink className="w-4 h-4" /></Button>
                    </a>
                    <Button size="sm" className="rounded-lg" disabled={busy}
                      onClick={() => manage(b.id)} data-testid="admin-manage-btn">
                      <Settings2 className="w-4 h-4 mr-1" /> Yönet
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg" disabled={busy}
                      onClick={() => act(b.id, "extend_trial", { days: 3 })} data-testid="admin-extend-btn">
                      <CalendarPlus className="w-4 h-4 mr-1" /> +3 gün
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg" disabled={busy}
                      onClick={() => { setAssign(b); setPlanId(subPlans[0]?.id || ""); }} data-testid="admin-assign-btn">
                      Plan Ata
                    </Button>
                    {b.is_suspended ? (
                      <Button size="sm" className="rounded-lg" disabled={busy} onClick={() => act(b.id, "activate")} data-testid="admin-activate-btn">
                        <Play className="w-4 h-4 mr-1" /> Aktifleştir
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-lg text-destructive border-destructive/30" disabled={busy}
                        onClick={() => act(b.id, "suspend")} data-testid="admin-suspend-btn">
                        <Ban className="w-4 h-4 mr-1" /> Askıya Al
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-lg" disabled={busy}
                      onClick={() => addCredits(b)} data-testid="admin-add-credits-btn" title="WhatsApp kontörü yükle">
                      <MessageCircle className="w-4 h-4 mr-1" /> Kontör
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-lg text-destructive hover:bg-destructive/10" disabled={busy}
                      onClick={() => del(b.id, b.name)} data-testid="admin-delete-btn" title="İşletmeyi kalıcı olarak sil">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!assign} onOpenChange={(o) => !o && setAssign(null)}>
        <DialogContent data-testid="assign-plan-dialog">
          <DialogHeader><DialogTitle>Plan Ata — {assign?.name}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger className="h-11" data-testid="assign-plan-select"><SelectValue placeholder="Plan seçin" /></SelectTrigger>
              <SelectContent>
                {subPlans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssign(null)}>İptal</Button>
            <Button disabled={busy || !planId} onClick={() => act(assign.id, "assign_plan", { plan_id: planId })} data-testid="assign-plan-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ata"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
